import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Mail,
  Lock,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  ShieldCheck,
  Gauge,
  Workflow,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { setAuthToken } from '@/lib/api';
import { getAuthStore } from '@/store/auth';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Publicação versionada',
    text: 'Cada formulário com histórico e trilha de auditoria.',
  },
  {
    icon: Gauge,
    title: 'Respostas ao vivo',
    text: 'Acompanhe envios e indicadores em tempo real.',
  },
  {
    icon: Workflow,
    title: 'Fluxos sem código',
    text: 'Monte jornadas condicionais em poucos minutos.',
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (localStorage.getItem('mks_forms_token')) {
    return <Navigate to="/forms" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Preencha e-mail e senha para continuar.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/login', {
        email: email.trim(),
        password,
      });
      setAuthToken(data.accessToken);
      getAuthStore().login(data.user);
      toast.success(`Bem-vindo, ${data.user?.firstName ?? 'usuário'}`);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/forms', { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        'Não foi possível entrar. Verifique suas credenciais.';
      setError(Array.isArray(message) ? message.join(' ') : message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white font-sans antialiased">
      {/* ── Painel de marca ─────────────────────────────────────────── */}
      <aside className="relative hidden w-[54%] shrink-0 overflow-hidden bg-[linear-gradient(150deg,#8a0044_0%,#c70065_46%,#e20074_100%)] lg:flex lg:flex-col lg:justify-between lg:px-14 lg:py-12 xl:w-[56%]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_74%_6%,rgba(255,255,255,0.20),transparent_52%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.055)_1px,transparent_1px)] bg-[size:32px_32px]" />
        <img
          src="/t-systems-t.svg"
          alt=""
          aria-hidden="true"
          draggable={false}
          className="pointer-events-none absolute -bottom-16 -right-14 w-80 select-none brightness-0 invert opacity-[0.07]"
        />

        <div className="relative">
          <img
            src="/t-systems.svg"
            alt="T-SYSTEMS"
            draggable={false}
            className="h-9 w-auto brightness-0 invert"
          />
        </div>

        <div className="relative max-w-[27rem]">
          <h2 className="text-[2.15rem] font-bold leading-[1.12] tracking-tight text-white">
            Formulários que a sua operação confia.
          </h2>
          <p className="mt-4 text-[0.95rem] leading-relaxed text-white/75">
            Crie, publique e acompanhe respostas em um só lugar — com a
            governança e a identidade da T-SYSTEMS.
          </p>

          <ul className="mt-9 space-y-5">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-3.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10">
                  <Icon className="h-4 w-4 text-white" strokeWidth={2} />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">
                    {title}
                  </span>
                  <span className="mt-0.5 block text-[0.82rem] leading-snug text-white/65">
                    {text}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-[0.72rem] font-medium tracking-wide text-white/45">
          © {new Date().getFullYear()} T-SYSTEMS · AIDA Forms
        </p>
      </aside>

      {/* ── Formulário ──────────────────────────────────────────────── */}
      <main className="relative flex w-full flex-1 items-center justify-center overflow-hidden px-6 py-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_560px_340px_at_88%_-12%,rgba(226,0,116,0.10),transparent)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_460px_320px_at_-8%_108%,rgba(199,0,101,0.055),transparent)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(28,26,31,0.07)_1px,transparent_1px)] bg-[size:22px_22px] [mask-image:radial-gradient(ellipse_58%_52%_at_50%_46%,#000_20%,transparent_75%)]" />

        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-end px-8 py-6">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#ece7ed] bg-white/70 px-3 py-1 text-[0.7rem] font-medium text-[#8b8590] backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[#12b76a]" />
            Ambiente seguro
          </span>
        </div>

        <div className="relative w-full max-w-[396px]">
          <img
            src="/t-systems.svg"
            alt="T-SYSTEMS"
            draggable={false}
            className="mb-9 h-7 w-auto lg:hidden"
          />

          <p className="mb-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#c70065]">
            AIDA Forms · T-SYSTEMS
          </p>
          <h1 className="text-[1.7rem] font-bold tracking-tight text-[#1c1a1f]">
            Bem-vindo de volta
          </h1>
          <p className="mt-1.5 text-sm text-[#6f6b75]">
            Acesse o painel administrativo de formulários.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
            {error && (
              <div
                role="alert"
                className="flex items-start gap-2.5 rounded-xl border border-[#e2007433] bg-[#e200740f] px-3.5 py-3"
              >
                <AlertCircle className="mt-px h-4 w-4 shrink-0 text-[#c70065]" />
                <p className="text-[0.8rem] font-medium leading-snug text-[#a30051]">
                  {error}
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-[0.8rem] font-semibold text-[#3d3944]"
              >
                E-mail
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-[#a8a3ad]" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@empresa.com"
                  className="h-11 w-full !rounded-xl border border-[#e2dde3] !bg-white !pl-11 !pr-3.5 !text-sm !text-[#1c1a1f] outline-none transition-colors placeholder:text-[#b3aeb8] hover:border-[#cfc8d1] focus:border-[#e20074] focus:ring-[3px] focus:ring-[rgba(226,0,116,0.13)]"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-[0.8rem] font-semibold text-[#3d3944]"
              >
                Senha
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[1.05rem] w-[1.05rem] -translate-y-1/2 text-[#a8a3ad]" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 w-full !rounded-xl border border-[#e2dde3] !bg-white !pl-11 !pr-11 !text-sm !text-[#1c1a1f] outline-none transition-colors placeholder:text-[#b3aeb8] hover:border-[#cfc8d1] focus:border-[#e20074] focus:ring-[3px] focus:ring-[rgba(226,0,116,0.13)]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-[#a8a3ad] transition-colors hover:bg-[#f8f6f8] hover:text-[#6f6b75]"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(95deg,#c70065_0%,#e20074_100%)] text-sm font-semibold text-white shadow-[0_10px_22px_-10px_rgba(226,0,116,0.75)] transition-all hover:shadow-[0_14px_28px_-10px_rgba(226,0,116,0.85)] hover:brightness-[1.05] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:brightness-100"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Entrando…
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-[0.72rem] leading-relaxed text-[#9d97a2]">
            Problemas para acessar? Fale com o administrador do sistema.
          </p>
        </div>
      </main>
    </div>
  );
}
